import { CanvasEvent, Canvas as GCanvas } from '@antv/g';
import { Renderer as CanvasRenderer } from '@antv/g-canvas';
import { Plugin as DragNDropPlugin } from '@antv/g-plugin-dragndrop';
import { createDOM } from '@antv/util';
import { getBBoxSize, getCombinedBBox } from '../utils/bbox';
import { parsePoint, toPointObject } from '../utils/point';
const layersName = ['background', 'main', 'label', 'transient'];
export class Canvas {
    extends;
    config;
    getConfig() {
        return this.config;
    }
    getLayer(layer = 'main') {
        return this.extends.layers[layer];
    }
    /**
     * <zh/> 获取所有图层
     *
     * <en/> Get all layers
     * @returns <zh/> 图层 <en/> Layer
     */
    getLayers() {
        return this.extends.layers;
    }
    /**
     * <zh/> 获取渲染器
     *
     * <en/> Get renderer
     * @param layer - <zh/> 图层 <en/> Layer
     * @returns <zh/> 渲染器 <en/> Renderer
     */
    getRenderer(layer) {
        return this.extends.renderers[layer];
    }
    /**
     * <zh/> 获取相机
     *
     * <en/> Get camera
     * @param layer - <zh/> 图层 <en/> Layer
     * @returns <zh/> 相机 <en/> Camera
     */
    getCamera(layer = 'main') {
        return this.getLayer(layer).getCamera();
    }
    getRoot(layer = 'main') {
        return this.getLayer(layer).getRoot();
    }
    getContextService(layer = 'main') {
        return this.getLayer(layer).getContextService();
    }
    setCursor(cursor) {
        this.config.cursor = cursor;
        this.getLayer().setCursor(cursor);
    }
    get document() {
        return this.getLayer().document;
    }
    get context() {
        return this.getLayer().context;
    }
    constructor(config) {
        this.config = config;
        const { renderer, background, cursor, ...restConfig } = config;
        const renderers = createRenderers(renderer);
        const layers = Object.fromEntries(layersName.map((layer) => {
            const canvas = new GCanvas({
                ...restConfig,
                supportsMutipleCanvasesInOneContainer: true,
                renderer: renderers[layer],
                background: layer === 'background' ? background : undefined,
            });
            return [layer, canvas];
        }));
        configCanvasDom(layers);
        this.extends = {
            config,
            renderer,
            renderers,
            layers,
        };
    }
    get ready() {
        return Promise.all(Object.entries(this.getLayers()).map(([, canvas]) => canvas.ready));
    }
    resize(width, height) {
        Object.assign(this.extends.config, { width, height });
        Object.values(this.getLayers()).forEach((canvas) => canvas.resize(width, height));
    }
    getBounds() {
        return getCombinedBBox(Object.values(this.getLayers())
            .map((canvas) => canvas.document.documentElement)
            .filter((el) => el.childNodes.length > 0)
            .map((el) => el.getBounds()));
    }
    getContainer() {
        const container = this.extends.config.container;
        return typeof container === 'string' ? document.getElementById(container) : container;
    }
    getSize() {
        return [this.extends.config.width || 0, this.extends.config.height || 0];
    }
    appendChild(child, index) {
        const layer = (child.style?.$layer || 'main');
        return this.getLayer(layer).appendChild(child, index);
    }
    setRenderer(renderer) {
        if (renderer === this.extends.renderer)
            return;
        const renderers = createRenderers(renderer);
        this.extends.renderers = renderers;
        Object.entries(renderers).forEach(([layer, instance]) => this.getLayer(layer).setRenderer(instance));
        configCanvasDom(this.getLayers());
    }
    getCanvasByViewport(point) {
        return parsePoint(this.getLayer().viewport2Canvas(toPointObject(point)));
    }
    getViewportByCanvas(point) {
        return parsePoint(this.getLayer().canvas2Viewport(toPointObject(point)));
    }
    getViewportByClient(point) {
        return parsePoint(this.getLayer().client2Viewport(toPointObject(point)));
    }
    getClientByViewport(point) {
        return parsePoint(this.getLayer().viewport2Client(toPointObject(point)));
    }
    getClientByCanvas(point) {
        return this.getClientByViewport(this.getViewportByCanvas(point));
    }
    getCanvasByClient(point) {
        const main = this.getLayer();
        const viewportPoint = main.client2Viewport(toPointObject(point));
        return parsePoint(main.viewport2Canvas(viewportPoint));
    }
    async toDataURL(options = {}) {
        const devicePixelRatio = window.devicePixelRatio || 1;
        const { mode = 'viewport', ...restOptions } = options;
        let [startX, startY, width, height] = [0, 0, 0, 0];
        if (mode === 'viewport') {
            [width, height] = this.getSize();
        }
        else if (mode === 'overall') {
            const bounds = this.getBounds();
            const size = getBBoxSize(bounds);
            [startX, startY] = bounds.min;
            [width, height] = size;
        }
        const container = createDOM('<div id="virtual-image"></div>');
        const offscreenCanvas = new GCanvas({
            width,
            height,
            renderer: new CanvasRenderer(),
            devicePixelRatio,
            container,
            background: this.extends.config.background,
        });
        await offscreenCanvas.ready;
        offscreenCanvas.appendChild(this.getLayer('background').getRoot().cloneNode(true));
        offscreenCanvas.appendChild(this.getRoot().cloneNode(true));
        // Handle label canvas
        const label = this.getLayer('label').getRoot().cloneNode(true);
        const originCanvasPosition = offscreenCanvas.viewport2Canvas({ x: 0, y: 0 });
        const currentCanvasPosition = this.getCanvasByViewport([0, 0]);
        label.translate([
            currentCanvasPosition[0] - originCanvasPosition.x,
            currentCanvasPosition[1] - originCanvasPosition.y,
        ]);
        label.scale(1 / this.getCamera().getZoom());
        offscreenCanvas.appendChild(label);
        offscreenCanvas.appendChild(this.getLayer('transient').getRoot().cloneNode(true));
        const camera = this.getCamera();
        const offscreenCamera = offscreenCanvas.getCamera();
        if (mode === 'viewport') {
            offscreenCamera.setZoom(camera.getZoom());
            offscreenCamera.setPosition(camera.getPosition());
            offscreenCamera.setFocalPoint(camera.getFocalPoint());
        }
        else if (mode === 'overall') {
            const [x, y, z] = offscreenCamera.getPosition();
            const [fx, fy, fz] = offscreenCamera.getFocalPoint();
            offscreenCamera.setPosition([x + startX, y + startY, z]);
            offscreenCamera.setFocalPoint([fx + startX, fy + startY, fz]);
        }
        const contextService = offscreenCanvas.getContextService();
        return new Promise((resolve) => {
            offscreenCanvas.addEventListener(CanvasEvent.RERENDER, async () => {
                // 等待图片渲染完成 / Wait for the image to render
                await new Promise((r) => setTimeout(r, 300));
                const url = await contextService.toDataURL(restOptions);
                resolve(url);
            });
        });
    }
    destroy() {
        Object.values(this.getLayers()).forEach((canvas) => {
            const camera = canvas.getCamera();
            camera.cancelLandmarkAnimation();
            canvas.destroy();
        });
    }
}
/**
 * <zh/> 创建渲染器
 *
 * <en/> Create renderers
 * @param renderer - <zh/> 渲染器创建器 <en/> Renderer creator
 * @returns <zh/> 渲染器 <en/> Renderer
 */
function createRenderers(renderer) {
    return Object.fromEntries(layersName.map((layer) => {
        const instance = renderer?.(layer) || new CanvasRenderer();
        if (layer === 'main') {
            instance.registerPlugin(new DragNDropPlugin({
                isDocumentDraggable: true,
                isDocumentDroppable: true,
                dragstartDistanceThreshold: 10,
                dragstartTimeThreshold: 100,
            }));
        }
        else {
            instance.unregisterPlugin(instance.getPlugin('dom-interaction'));
        }
        return [layer, instance];
    }));
}
/**
 * <zh/> 配置画布 DOM
 *
 * <en/> Configure canvas DOM
 * @param layers - <zh/> 画布 <en/> Canvas
 */
function configCanvasDom(layers) {
    Object.entries(layers).forEach(([layer, canvas]) => {
        const domElement = canvas.getContextService().getDomElement();
        domElement.style.position = 'absolute';
        domElement.style.outline = 'none';
        domElement.tabIndex = 1;
        if (layer !== 'main')
            domElement.style.pointerEvents = 'none';
    });
}
//# sourceMappingURL=canvas.js.map